import { useState, useEffect, useRef } from "react";
import { Chip, Checkbox } from "@heroui/react";
import { Tag } from "assets/icons";
import { AvailableLabel, AvailableLabels } from "../addTaskModal/addTaskModal";
import { TaskLabel, TaskLabels } from "shared/types/task";
import { getIconForLabel } from "shared/utils/get-icons";

interface LabelSelectionProps {
  taskLabels: AvailableLabels | TaskLabels[];
  setTaskLabels:
    | React.Dispatch<React.SetStateAction<AvailableLabels>>
    | React.Dispatch<React.SetStateAction<TaskLabels[]>>;
  availableLabels: AvailableLabels;
}

const LabelSelection: React.FC<LabelSelectionProps> = ({
  taskLabels,
  setTaskLabels,
  availableLabels,
}) => {
  const [isLabelDropdownOpen, setLabelDropdownOpen] = useState(false);
  const [labelSearchInput, setLabelSearchInput] = useState("");
  const labelDropdownRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleLabelSearchChange = (e: any) => {
    setLabelSearchInput(e.target.value);
  };

  const toggleLabel = (labelName: TaskLabel) => {
    if (taskLabels.some((label) => label.name === labelName)) {
      setTaskLabels((prevLabels: any) =>
        prevLabels.filter((lbl: any) => lbl.name !== labelName),
      );
    } else {
      // construct an AvailableLabel object
      const newLabel: AvailableLabel = {
        name: labelName,
        icon: getIconForLabel(labelName),
      };
      setTaskLabels((prevLabels: any) => [...prevLabels, newLabel]);
    }
  };

  const handleClickOutside = (event: any) => {
    if (
      labelDropdownRef.current &&
      !labelDropdownRef.current.contains(event.target)
    ) {
      setLabelDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Focus on search input when dropdown is opened
  useEffect(() => {
    if (isLabelDropdownOpen) {
      searchInputRef.current?.focus();
    }
  }, [isLabelDropdownOpen]);

  return (
    <div className="relative z-50">
      <div
        className="flex cursor-pointer items-center gap-2"
        onClick={() => setLabelDropdownOpen(!isLabelDropdownOpen)}
      >
        <Chip
          className="p-0"
          variant={taskLabels.length === 0 ? "light" : "solid"}
        >
          <div className="flex items-center gap-2">
            <Tag className="h-[20px] w-[20px]" />
            {taskLabels.length === 0 && "Kies tags"}
            {taskLabels.length === 1 && taskLabels[0].name}
            {taskLabels.length > 1 && `${taskLabels.length} labels`}
          </div>
        </Chip>
      </div>

      {/* Label Dropdown (Modal) */}
      {isLabelDropdownOpen && (
        <div
          ref={labelDropdownRef}
          className="shadow-lg absolute left-0 z-10 mt-2 rounded-md bg-white p-4"
        >
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Zoek tags..."
            value={labelSearchInput}
            onChange={handleLabelSearchChange}
            className="mb-4 p-2"
          />

          {availableLabels
            .filter((label) =>
              label.name.toLowerCase().includes(labelSearchInput.toLowerCase()),
            )
            .map((label) => {
              return (
                <div
                  key={label.name}
                  className="mb-2 flex items-center justify-between"
                >
                  <div className="mr-3 flex items-center gap-2">
                    {label.icon}
                    <p>{label.name}</p>
                  </div>
                  <Checkbox
                    isSelected={taskLabels.some(
                      (taskLabel) => taskLabel.name === label.name,
                    )}
                    onChange={() => toggleLabel(label.name)}
                  />
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default LabelSelection;
